import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { DataProvider, useAppData } from './contexts/DataContext';
import LoginPage from './components/LoginPage';
import Sidebar from './components/Sidebar';
import CustomerGrid from './components/CustomerGrid';
import CustomerModal from './components/CustomerModal';
import DeleteModal from './components/DeleteModal';
import GlobalSearchModal from './components/GlobalSearchModal';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { I18nProvider, useT } from './contexts/I18nContext';
import { exportToExcel } from './utils/exportExcel';
import ImportCustomersModal from './components/ImportCustomersModal';
import { auth } from './firebase';
import { apiFetch } from './services/api';
import { NO_ORIGIN } from './services/customerSources';
import { saveCustomer, deleteCustomer } from './utils/customerApi';
import * as checkboxService from './services/checkboxService';
import * as paylinkService from './services/paylinkService';
import './App.css';

/* ── Lazy-loaded page components ── */
const DashboardPage = lazy(() => import('./components/DashboardPage'));
const CategoriesPage = lazy(() => import('./components/CategoriesPage'));
const StaffPage = lazy(() => import('./components/StaffPage'));
const SchedulePage = lazy(() => import('./components/SchedulePage'));
const ScheduleTemplatesPage = lazy(() => import('./components/ScheduleTemplatesPage'));
const ServicesPage = lazy(() => import('./components/ServicesPage'));
const CalendarPage = lazy(() => import('./components/CalendarPage'));
const FinancesPage = lazy(() => import('./components/FinancesPage'));
const ReportsPage = lazy(() => import('./components/ReportsPage'));
const CashRegisterPage = lazy(() => import('./components/CashRegisterPage'));
const InventoryProductsPage = lazy(() => import('./components/InventoryProductsPage'));
const InventoryStockPage = lazy(() => import('./components/InventoryStockPage'));
const InventoryPurchasesPage = lazy(() => import('./components/InventoryPurchasesPage'));
const InventoryMovementsPage = lazy(() => import('./components/InventoryMovementsPage'));
const OrdersPage = lazy(() => import('./components/OrdersPage'));
const QuickSalePage = lazy(() => import('./components/QuickSalePage'));
const ProductionPage = lazy(() => import('./components/ProductionPage'));
const SalarySchemesPage = lazy(() => import('./components/SalarySchemesPage'));
const SalaryCalcPage = lazy(() => import('./components/SalaryCalcPage'));
const FinancialResultPage = lazy(() => import('./components/FinancialResultPage'));
const BusinessMonitorPage = lazy(() => import('./components/BusinessMonitorPage'));
const TariffPlansPage = lazy(() => import('./components/TariffPlansPage'));
const RolesPage = lazy(() => import('./components/RolesPage'));
const UsersPage = lazy(() => import('./components/UsersPage'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const CheckboxSettingsPage = lazy(() => import('./components/CheckboxSettingsPage'));
const TelegramSettingsPage = lazy(() => import('./components/TelegramSettingsPage'));
const NotificationsAutoPage = lazy(() => import('./components/NotificationsAutoPage'));
const CommunicationPage = lazy(() => import('./components/CommunicationPage'));
const SmsSettingsPage = lazy(() => import('./components/SmsSettingsPage'));
const EmailSettingsPage = lazy(() => import('./components/EmailSettingsPage'));
const LocationsSettingsPage = lazy(() => import('./components/LocationsSettingsPage'));
const NovaPoshtaSettingsPage = lazy(() => import('./components/NovaPoshtaSettingsPage'));
const MeestExpressSettingsPage = lazy(() => import('./components/MeestExpressSettingsPage'));
const RozetkaSettingsPage = lazy(() => import('./components/RozetkaSettingsPage'));
const PromSettingsPage = lazy(() => import('./components/PromSettingsPage'));
const ShopifySettingsPage = lazy(() => import('./components/ShopifySettingsPage'));
const OlxSettingsPage = lazy(() => import('./components/OlxSettingsPage'));
const InstagramSettingsPage = lazy(() => import('./components/InstagramSettingsPage'));
const MessengerSettingsPage = lazy(() => import('./components/MessengerSettingsPage'));
const OnlineBookingSettingsPage = lazy(() => import('./components/OnlineBookingSettingsPage'));
const LeadsSettingsPage = lazy(() => import('./components/LeadsSettingsPage'));
const IntegrationKeysPage = lazy(() => import('./components/IntegrationKeysPage'));
const TelephonySettingsPage = lazy(() => import('./components/TelephonySettingsPage'));
const DocumentTemplatesPage = lazy(() => import('./components/DocumentTemplatesPage'));
const CallsPage = lazy(() => import('./components/calls/CallsPage'));
const CallPopup = lazy(() => import('./components/calls/CallPopup'));
const AuditLogPage = lazy(() => import('./components/AuditLogPage'));
const TasksPage = lazy(() => import('./components/TasksPage'));
const ProjectsPage = lazy(() => import('./components/ProjectsPage'));
const LoyaltyPage = lazy(() => import('./components/LoyaltyPage'));
// OnboardingScreen removed — business type is now selected during registration
const MyPlanPage = lazy(() => import('./components/MyPlanPage'));
const CheckoutPage = lazy(() => import('./components/CheckoutPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));

/* ── Known views (for 404 detection) ── */
const KNOWN_VIEWS = new Set([
  'dashboard','clients','calendar','schedule','schedule_templates',
  'categories','services','staff','finances','reports','cash_register',
  'inventory_products','inventory_stock','inventory_purchases','inventory_movements',
  'orders','pos','production','salary_schemes','salary_calc','financial_result','business_monitor',
  'tariff_plans','roles','users','checkbox_settings','telegram_settings','notifications_auto',
  'sms_settings','email_settings','nova_poshta_settings','meest_express_settings','rozetka_settings','prom_settings','shopify_settings','locations_settings',
  'audit_log','loyalty','communication','my_plan','checkout','online_booking_settings','leads_settings','api_keys','help',
  'tasks','projects','calls','telephony_settings','document_templates',
]);

/* ── Page loading fallback ── */
function PageLoader() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'40vh', color:'rgba(255,255,255,0.5)', fontSize:'1rem', gap:'0.5rem' }}>
      <div style={{ width:24, height:24, border:'2px solid rgba(255,255,255,0.1)', borderTopColor:'#5F9598', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
    </div>
  );
}

function AppContent() {
  const { t } = useT();
  const toast = useToast();
  const { currentUser, userProfile, loading: authLoading, hasPermission, role, isSuperAdmin, impersonatedTenantId } = useAuth();
  const {
    loading, ensureGroups, readOnly,
    businessType, businessTypeLoaded,
    locations, setLocations,
    activeLocationId, setActiveLocationId,
    categories, setCategories,
    importanceLevels, setImportanceLevels,
    // customers not in DataContext — self-fetch pattern everywhere.
    staff: allStaff, setStaff,
    serviceGroups, setServiceGroups,
    services, setServices,
    appointments: allAppointments, setAppointments,
    transactions: allTransactions, setTransactions,
    scheduleTemplates, setScheduleTemplates,
    // cashShifts not in DataContext — CashRegisterPage self-fetches.
    productCategories, setProductCategories,
    products, setProducts,
    warehouses: allWarehouses, setWarehouses,
    stock: allStock, setStock,
    suppliers, setSuppliers,
    salarySchemes, setSalarySchemes,
    // salaryHistory not in DataContext — SalaryCalcPage/BusinessMonitor/
    // FinancialResult self-fetch.
    fixedExpenses, setFixedExpenses,
    // customerDocuments not in DataContext — CustomerModal / AppointmentModal
    // self-fetch by customerPhone.
    tariffPlans, setTariffPlans,
    orders: allOrders, setOrders,
    techCards, setTechCards,
    // productions not in DataContext — ProductionPage self-fetches.
    unreadMsgCount, setUnreadMsgCount,
    loyaltyPrograms, setLoyaltyPrograms,
    loyaltyBalances, setLoyaltyBalances,
    coupons, setCoupons,
    // couponUsages not in DataContext — LoyaltyPage self-fetches.
    subscriptionPlans, setSubscriptionPlans,
    // customerSubscriptions not in DataContext — pages self-fetch (Phase A6).
    groupsReady,
  } = useAppData();

  /* ── Init Checkbox session per location (re-init on location switch) ── */
  useEffect(() => {
    if (!currentUser) return;
    checkboxService.clearSession();
    paylinkService.configure({ deviceId: null });
    const locId = activeLocationId || 1;
    apiFetch(`/api/checkbox?action=init&locationId=${locId}`).then(res => {
      if (res?.configured && res.access_token) {
        checkboxService.setCredentials(res.license_key, '__from_server__');
        checkboxService.setToken(res.access_token, res.license_key);
      }
      if (res?.defaultTaxCodes) {
        checkboxService.setDefaultTaxCodes(res.defaultTaxCodes);
      }
    }).catch(() => {});
    // Init PayLink from per-location settings (fallback to legacy global)
    const settingsKey = `checkbox_${locId}`;
    apiFetch(`/api/settings?key=${settingsKey}`).then(async (res) => {
      let data = res?.data;
      if (!data?.paylinkDeviceId) {
        const fallback = await apiFetch('/api/settings?key=checkbox').catch(() => null);
        data = fallback?.data;
      }
      if (data?.paylinkDeviceId) {
        paylinkService.configure({
          host: data.paylinkHost || 'localhost',
          port: data.paylinkPort || 9020,
          deviceId: data.paylinkDeviceId,
        });
      }
    }).catch(() => {});
  }, [currentUser, activeLocationId]);

  /* ── Birthday bonuses — fully server-side now. Endpoint is idempotent
     (deterministic tx ID lt_bday_{year}_{custId}) so calling from every
     tab on mount is safe. Server bumps _meta/sync for loyalty on award,
     which triggers our realtime refetch of balances + ledger. Runs once
     per session per tenant, guarded by a ref so tab-focus re-renders
     don't re-fire it. ── */
  const birthdayCheckFired = useRef(false);
  useEffect(() => {
    if (!currentUser || birthdayCheckFired.current) return;
    birthdayCheckFired.current = true;
    apiFetch('/api/loyalty/process-birthday-bonuses', { method: 'POST', body: {} })
      .catch(() => { /* logged server-side; balance refetch still happens on next sync */ });
  }, [currentUser]);

  /* ── Location-filtered data ── */
  const locId = activeLocationId;
  const locIdStr = String(locId);
  const staff = allStaff.filter(s => !s.locationIds || s.locationIds.length === 0 || s.locationIds.some(lid => String(lid) === locIdStr));
  const appointments = allAppointments.filter(a => String(a.locationId) === locIdStr);
  const transactions = allTransactions.filter(t => String(t.locationId) === locIdStr);
  // cashShifts filter moved into CashRegisterPage (self-fetch with locationId).
  // Warehouses: show only current location's warehouses in UI
  const warehouses = allWarehouses.filter(w => String(w.locationId) === locIdStr);
  // Stock: filter by warehouses of current location
  const locWarehouseIds = new Set(warehouses.map(w => String(w.id)));
  const stock = allStock.filter(s => locWarehouseIds.has(String(s.warehouseId)));
  // stockMovements is no longer loaded globally — Inventory pages fetch
  // their own slice. Hot paths (POS / orders / appointments / production)
  // hit narrow server endpoints. The per-location filter that used to live
  // here is unneeded: consumers either already scope by warehouse on the
  // server (fifo-peek), or the location filter applies on their own data.
  const orders = allOrders.filter(o => String(o.locationId) === locIdStr);
  // productions filter moved into ProductionPage (self-fetch with locationId param).

  /* Auto-stamp locationId + createdBy on new records */
  const authorStamp = currentUser ? { uid: currentUser.uid, name: userProfile?.displayName || currentUser.email || '' } : null;
  const stampLocation = (setter) => (updater) => {
    setter(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (!Array.isArray(next)) return next;
      return next.map(item => {
        const existed = prev.some(p => p.id === item.id);
        if (!existed) {
          const stamped = { ...item };
          if (!stamped.locationId) stamped.locationId = locId;
          if (!stamped.createdBy && authorStamp) stamped.createdBy = authorStamp;
          // createdAt is required for windowed Firestore loading
          // (.where('createdAt','>',cutoff) excludes docs without the field)
          if (!stamped.createdAt) stamped.createdAt = Date.now();
          return stamped;
        }
        return item;
      });
    });
  };
  const setAppointmentsStamped = stampLocation(setAppointments);
  const setTransactionsStamped = stampLocation(setTransactions);
  // setCashShiftsStamped removed — CashRegisterPage stamps + saves directly.
  // setStockMovementsStamped removed — stock_movements writes go via direct API
  const setOrdersStamped = stampLocation(setOrders);
  // setProductionsStamped removed — productions writes are stamped
  // inside ProductionPage before saveDoc.
  const setWarehousesStamped = stampLocation(setWarehouses);

  /* Navigation — driven by URL */
  const location = useLocation();
  const navigate  = useNavigate();
  const currentView = location.pathname.replace(/^\//, '') || userProfile?.homePage || 'dashboard';
  const setCurrentView = (view) => navigate('/' + view);

  /* ── Lazy-load data groups based on current page ── */
  useEffect(() => {
    const PAGE_GROUPS = {
      dashboard:            ['calendar', 'finance', 'inventory', 'orders', 'loyalty'],
      calendar:             ['calendar', 'inventory', 'finance', 'documents', 'loyalty'],
      clients:              ['calendar', 'finance', 'loyalty', 'inventory'],
      staff:                ['calendar', 'salary'],
      services:             ['calendar', 'inventory'],
      schedule:             ['calendar'],
      schedule_templates:   ['calendar'],
      finances:             ['finance'],
      cash_register:        ['finance'],
      financial_result:     ['calendar', 'finance', 'inventory', 'salary'],
      business_monitor:     ['calendar', 'finance', 'inventory', 'salary', 'orders'],
      inventory_products:   ['inventory', 'orders'],
      inventory_stock:      ['inventory'],
      inventory_purchases:  ['inventory'],
      inventory_movements:  ['inventory'],
      pos:                  ['inventory', 'finance', 'loyalty'],
      orders:               ['orders', 'inventory', 'finance', 'loyalty'],
      production:           ['production', 'inventory'],
      salary_schemes:       ['salary'],
      salary_calc:          ['calendar', 'finance', 'salary'],
      reports:              ['calendar', 'finance', 'inventory', 'loyalty'],
      communication:        [],
      loyalty:              ['loyalty', 'finance', 'calendar'],
    };
    const groups = PAGE_GROUPS[currentView];
    if (groups && groups.length > 0) {
      ensureGroups(...groups);
    }
  }, [currentView, ensureGroups]);

  // Page is ready when all required groups are loaded
  const PAGE_GROUPS_MAP = {
    dashboard: ['calendar', 'finance', 'inventory', 'orders', 'loyalty'],
    calendar: ['calendar'], clients: ['calendar'],
    staff: ['calendar'], services: ['calendar', 'inventory'],
    schedule: ['calendar'], schedule_templates: ['calendar'],
    finances: ['finance'], cash_register: ['finance'],
    financial_result: ['calendar', 'finance', 'inventory', 'salary'],
    business_monitor: ['calendar', 'finance', 'inventory', 'salary', 'orders'],
    inventory_products: ['inventory'], inventory_stock: ['inventory'],
    inventory_purchases: ['inventory'], inventory_movements: ['inventory'],
    pos: ['inventory', 'finance', 'loyalty'],
    orders: ['orders', 'inventory'], production: ['production', 'inventory'],
    salary_schemes: ['salary'], salary_calc: ['calendar', 'finance', 'salary'],
    reports: ['calendar', 'finance', 'inventory', 'loyalty'],
    loyalty: ['loyalty', 'finance', 'calendar'],
  };
  const requiredGroups = PAGE_GROUPS_MAP[currentView] || [];
  const pageReady = requiredGroups.length === 0 || requiredGroups.every(g => groupsReady[g]);

  /* Mobile sidebar toggle */
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleNavigate = (view) => {
    setCurrentView(view);
    setSidebarOpen(false);
    window.scrollTo(0, 0);
  };

  /* Tenant blocked overlay */
  const [tenantBlocked, setTenantBlocked] = useState(null);
  useEffect(() => {
    const handler = (e) => setTenantBlocked(e.detail || 'blocked');
    window.addEventListener('tenant-blocked', handler);
    return () => window.removeEventListener('tenant-blocked', handler);
  }, []);

  /* Scheduled deletion — the tenant is inside the grace period. Access is
     already gone (status !== 'active'), but nothing has been erased yet, so
     the overlay has to offer the undo. Details are admin-only, same as the
     cancel endpoint; for anyone else the generic wording stands. */
  const [deletionInfo, setDeletionInfo] = useState(null);
  const [cancellingDeletion, setCancellingDeletion] = useState(false);
  useEffect(() => {
    if (tenantBlocked !== 'deleting') { setDeletionInfo(null); return undefined; }
    let cancelled = false;
    apiFetch('/api/delete-account?action=status')
      .then(r => { if (!cancelled) setDeletionInfo(r); })
      .catch(() => { /* not an admin — nothing to offer */ });
    return () => { cancelled = true; };
  }, [tenantBlocked]);

  const handleCancelDeletion = async () => {
    setCancellingDeletion(true);
    try {
      await apiFetch('/api/delete-account?action=cancel', { method: 'POST', body: {} });
      window.location.reload();
    } catch (err) {
      toast.error(err?.message || t('blocked.cancelDeletionFailed', 'Не вдалося скасувати видалення'));
      setCancellingDeletion(false);
    }
  };
  const deletionDate = deletionInfo?.scheduledFor
    ? new Intl.DateTimeFormat('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' })
        .format(new Date(deletionInfo.scheduledFor))
    : null;

  /* Rate-limit toast — debounced so a burst of 429s doesn't spam 10 toasts */
  useEffect(() => {
    let lastShown = 0;
    const handler = (e) => {
      const now = Date.now();
      if (now - lastShown < 10_000) return;
      lastShown = now;
      const secs = e.detail?.retryAfter || 60;
      toast.error(t('errors.rateLimited', { seconds: secs }));
    };
    window.addEventListener('api-rate-limited', handler);
    return () => window.removeEventListener('api-rate-limited', handler);
  }, [toast, t]);

  /* Server-error toast — fires after apiFetch exhausts its retry (5xx or
     network error). Debounced so a single incident doesn't produce 10 toasts
     from parallel in-flight requests. */
  useEffect(() => {
    let lastShown = 0;
    const handler = (e) => {
      const now = Date.now();
      if (now - lastShown < 8_000) return;
      lastShown = now;
      const status = e.detail?.status;
      const key = status === 0 ? 'errors.networkError' : 'errors.serverError';
      toast.error(t(key));
    };
    window.addEventListener('api-server-error', handler);
    return () => window.removeEventListener('api-server-error', handler);
  }, [toast, t]);

  /* Global Search (Ctrl+K) */
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowGlobalSearch(s => !s);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* Calendar state */
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [bookingCustomer, setBookingCustomer] = useState(null);
  const [orderingCustomer, setOrderingCustomer] = useState(null);
  const [focusAppointmentId, setFocusAppointmentId] = useState(null);
  const [focusOrderId, setFocusOrderId] = useState(null);

  /* Customer UI state */
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  // «Джерело» (23.10): фільтр списку клієнтів; опції — список закладу і
  // скільки клієнтів з кожного (разом із визначеними системою).
  const [originFilter, setOriginFilter] = useState('');
  const [originStats, setOriginStats] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('clients_view') || 'grid');

  // Server-paginated clients list state. Decoupled from DataContext
  // `customers` so the clients view can scale past tens of thousands —
  // DataContext still holds the full array for other consumers during
  // the migration (Phase 4 migrates lookups; Phase 5 drops it entirely).
  const [listCustomers, setListCustomers] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listVipCount, setListVipCount] = useState(0);
  const [listHasMore, setListHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listReloadTick, setListReloadTick] = useState(0);
  const listPageRef = useRef(0);
  const bumpListReload = useCallback(() => setListReloadTick(t => t + 1), []);

  // Reset list on search / category change; refetch page 0.
  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    listPageRef.current = 0;
    const t = setTimeout(() => {
      const params = new URLSearchParams({ page: '0', limit: '50' });
      if (search) params.set('q', search);
      if (categoryFilter) params.set('category', categoryFilter);
      if (originFilter) params.set('origin', originFilter);
      apiFetch(`/api/analytics/customers-index?${params}`)
        .then(res => {
          if (cancelled) return;
          setListCustomers(res.customers || []);
          setListTotal(res.total || 0);
          setListVipCount(res.vipCount || 0);
          setListHasMore(!!res.hasMore);
        })
        .catch(() => {
          if (cancelled) return;
          setListCustomers([]); setListTotal(0); setListVipCount(0); setListHasMore(false);
        })
        .finally(() => { if (!cancelled) setListLoading(false); });
    }, search ? 200 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, categoryFilter, originFilter, listReloadTick]);

  useEffect(() => {
    if (currentView !== 'clients' || !hasPermission('clients_view')) return undefined;
    let cancelled = false;
    apiFetch('/api/customers/sources?counts=1')
      .then(r => { if (!cancelled) setOriginStats(r); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentView, listReloadTick, hasPermission]);
  const originOptions = useMemo(() => {
    if (!originStats) return [];
    const counts = new Map((originStats.counts || []).map(c => [c.origin, c.n]));
    const names = [...(originStats.sources || [])];
    for (const c of originStats.counts || []) if (c.origin && !names.includes(c.origin)) names.push(c.origin);
    return names.map(n => ({ value: n, label: counts.get(n) ? `${n} (${counts.get(n)})` : n }))
      .concat(counts.get(null) ? [{ value: NO_ORIGIN, label: `${t('clients.noOrigin', 'Без джерела')} (${counts.get(null)})` }] : []);
  }, [originStats, t]);

  const loadMoreClients = useCallback(async () => {
    if (listLoading || !listHasMore) return;
    const next = listPageRef.current + 1;
    setListLoading(true);
    try {
      const params = new URLSearchParams({ page: String(next), limit: '50' });
      if (search) params.set('q', search);
      if (categoryFilter) params.set('category', categoryFilter);
      if (originFilter) params.set('origin', originFilter);
      const res = await apiFetch(`/api/analytics/customers-index?${params}`);
      setListCustomers(prev => [...prev, ...(res.customers || [])]);
      setListHasMore(!!res.hasMore);
      listPageRef.current = next;
    } catch {
      // Keep current list; next scroll tick will retry.
    } finally {
      setListLoading(false);
    }
  }, [listLoading, listHasMore, search, categoryFilter, originFilter]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [modalInitialTab, setModalInitialTab] = useState('main');
  const [modalInitialMode, setModalInitialMode] = useState('edit');
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  // Нова картка з анонімного треду «Комунікації»: форма заповнена даними
  // відвідувача, а після збереження тред привʼязується до нової картки.
  // Досі клік на ім'я такого треду відкривав форму з псевдо-клієнтом, чий
  // id — ключ треду («anon:…»), і збереження створювало картку з цим id —
  // тред після привʼязки зникав зі списку (29.09).
  const [modalPrefill, setModalPrefill] = useState(null);
  const threadLinkRef = useRef(null);
  useEffect(() => {
    if (!modalOpen) { setModalPrefill(null); threadLinkRef.current = null; }
  }, [modalOpen]);
  const handleCreateFromThread = useCallback(({ prefill, sessionId, onCreated }) => {
    setEditingCustomer(null);
    setModalPrefill(prefill || null);
    threadLinkRef.current = sessionId ? { sessionId, onCreated } : null;
    setModalInitialTab('main');
    setModalInitialMode('edit');
    setModalOpen(true);
  }, []);

  /* CRUD handlers */
  const handleCreate = () => { setEditingCustomer(null); setModalInitialTab('main'); setModalInitialMode('edit'); setModalOpen(true); };
  const handleEdit = (c) => { setEditingCustomer(c); setModalInitialTab('main'); setModalInitialMode('edit'); setModalOpen(true); };
  const handleView = (c) => { setEditingCustomer(c); setModalInitialTab('main'); setModalInitialMode('view'); setModalOpen(true); };
  const handleBook = (c) => { setBookingCustomer(c); handleNavigate('calendar'); };
  const handleOrder = (c) => { setOrderingCustomer(c); handleNavigate('orders'); };
  const handleSubscription = (c) => { setEditingCustomer(c); setModalInitialTab('loyalty'); setModalInitialMode('view'); setModalOpen(true); };
  const handleNavigateToTransaction = (tx) => {
    if (tx.appointmentId) {
      const apt = appointments.find(a => a.id === tx.appointmentId);
      if (apt) {
        if (apt.date) setSelectedDate(new Date(apt.date + 'T00:00:00'));
        setFocusAppointmentId(tx.appointmentId);
        handleNavigate('calendar');
        return;
      }
      // Old appointment (outside 60-day Firestore window) — use transaction date
      // to navigate; CalendarPage's SQL fallback will load that month's data.
      const navDate = tx.date || (tx.createdAt ? new Date(tx.createdAt).toISOString().slice(0, 10) : null);
      if (!navDate) { toast.info(t('finResult.sourceDeleted')); return; }
      setSelectedDate(new Date(navDate + 'T00:00:00'));
      setFocusAppointmentId(tx.appointmentId);
      handleNavigate('calendar');
    } else if (tx.orderId) {
      const ord = allOrders.find(o => String(o.id) === String(tx.orderId));
      if (ord) {
        setFocusOrderId(tx.orderId);
        handleNavigate('orders');
        return;
      }
      // Old order (outside 90-day Firestore window) — still navigate; OrdersPage
      // will fetch historical orders if user expands the period filter.
      setFocusOrderId(tx.orderId);
      handleNavigate('orders');
    }
  };
  const handleOpenCustomerByPhone = async (phone) => {
    if (!phone) return;
    const digits = String(phone).replace(/\D/g, '');
    if (!digits) return;
    // Match by normalized phone — server does digit comparison. Телефонія
    // (Binotel) дає номер як 0XXXXXXXXX, а картки зберігають 380XXXXXXXXX —
    // шукаємо обидва варіанти, інакше клік по імені в «Дзвінках» нічого не
    // відкривав (30.10).
    const variants = digits.length === 10 && digits.startsWith('0') ? [digits, `38${digits}`] : [digits];
    try {
      const res = await apiFetch(`/api/analytics/customers-by-phones?phones=${encodeURIComponent(variants.join(','))}`);
      const c = res?.customers?.[0];
      if (c) handleView(c);
      else toast.info('Картку з цим номером не знайдено');
    } catch { /* silent — opens nothing if lookup fails */ }
  };
  // Дзвінок з невідомого номера → нова картка з уже вписаним телефоном.
  const handleCreateWithPhone = useCallback((phone) => {
    handleCreateFromThread({ prefill: { phone: String(phone || ''), fromCall: true } });
  }, [handleCreateFromThread]);
  const handleDeleteRequest = async (id, name) => {
    setDeleteModal({ open: true, id, name: name || '', checking: true, hasRecords: false });
    const has = await customerHasRecords(id);
    // Fail-closed: SQL check unavailable → block delete (treat as has-records).
    // Server's own gate also fail-closes, so a direct DELETE would be rejected
    // anyway — this just keeps the UI honest and avoids a confusing 500 toast.
    setDeleteModal(prev => prev.id === id
      ? { ...prev, checking: false, hasRecords: has === 'error' ? true : has }
      : prev
    );
  };
  // Check via SQL if customer has any related document — appointments
  // (any status, including cancelled/no_show), transactions, or orders.
  // Source: hasAnyRefs from /api/analytics/customer-stats. Returns true,
  // false, or 'error' when the SQL check itself fails (fail-closed).
  const customerHasRecords = async (id) => {
    if (!id) return false;
    try {
      const res = await apiFetch(`/api/analytics/customer-stats?customerId=${encodeURIComponent(id)}`);
      return res.hasAnyRefs === true;
    } catch (err) {
      console.warn('[customerHasRecords] SQL check failed:', err);
      return 'error';
    }
  };
  const handleDeleteConfirm = async () => {
    if (deleteModal.hasRecords) return;
    try {
      await deleteCustomer(deleteModal.id);
      bumpListReload();
      setDeleteModal({ open: false, id: null });
    } catch (err) {
      // 409 from server gate — UI pre-check missed (race or stale data).
      // Reopen as cannot-delete instead of generic error toast.
      if (err?.status === 409 || /has_refs/.test(err?.message || '')) {
        setDeleteModal(prev => prev.open
          ? { ...prev, hasRecords: true }
          : prev
        );
        return;
      }
      toast.error(err?.message || 'Помилка видалення клієнта');
    }
  };
  const handleSave = async (data) => {
    try {
      if (editingCustomer) {
        await saveCustomer({ ...data, id: editingCustomer.id });
      } else {
        const customer = { ...data, id: String(Date.now()), createdAt: Date.now() };
        await saveCustomer(customer);
        const link = threadLinkRef.current;
        if (link?.sessionId) {
          threadLinkRef.current = null;
          // Картка вже є; збій привʼязки не має її губити — лише кажемо про нього.
          const tg = String(link.sessionId).match(/^telegram_(-?\d+)$/);
          try {
            const r = tg
              ? await apiFetch('/api/telegram?action=attach-chat', { method: 'POST', body: { chatId: Number(tg[1]), customerId: customer.id } })
              : await apiFetch('/api/threads?action=attach', { method: 'POST', body: { sessionId: link.sessionId, customerId: customer.id } });
            toast.success(`${t('comm.attachDone', 'Привʼязано до')} ${customer.name}${r?.moved ? ` · ${r.moved}` : ''}`);
            link.onCreated?.(customer.id);
          } catch (err) {
            toast.error(`${t('comm.cardCreatedNotLinked', 'Картку створено, але тред не привʼязано')}: ${err?.message || ''}`);
          }
        }
      }
      bumpListReload();
      setModalOpen(false);
    } catch (err) {
      // 409 duplicate_phone — server returns the existing customer who
      // already owns the phone. Surface a helpful message + 'Open' action
      // so the user can jump to that customer instead of guessing where
      // they are in the list.
      if (err?.status === 409 && err?.body?.error === 'duplicate_phone') {
        const existing = err.body.existing;
        const msg = existing?.name
          ? t('clients.duplicatePhoneWithName', { name: existing.name })
          : t('clients.duplicatePhone');
        if (existing?.id) {
          toast.error(msg, {
            action: {
              label: t('clients.openExisting'),
              onClick: async () => {
                // Close the new-customer modal, fetch full record for the
                // existing customer, reopen modal in edit mode against it.
                setModalOpen(false);
                try {
                  const res = await apiFetch('/api/analytics/customers-by-ids', {
                    method: 'POST',
                    body: { ids: [existing.id] },
                  });
                  const customer = res?.customers?.[0];
                  if (customer) {
                    setEditingCustomer(customer);
                    setModalInitialTab('main');
                    setModalInitialMode('edit');
                    setModalOpen(true);
                  }
                } catch { /* fallback: user finds them in the list manually */ }
              },
            },
          });
        } else {
          toast.error(msg);
        }
        return;
      }
      // Той самий нік Instagram уже в іншій картці: за ніком Direct сам
      // лягає на картку, тож двох власників одного ніку бути не може.
      if (err?.status === 409 && err?.body?.error === 'duplicate_instagram') {
        const existing = err.body.existing;
        toast.error(existing?.name
          ? t('clients.duplicateInstagramWithName', { name: existing.name })
          : t('clients.duplicateInstagram'));
        return;
      }
      toast.error(err?.message || 'Помилка збереження клієнта');
    }
  };

  // `filtered` used to be a client-side filter/sort over the whole
  // customers array. Server-paginated now via listCustomers — kept as
  // an alias so the surrounding JSX (Excel export, "shown X of Y"
  // toolbar) touches the least possible surface.
  const filtered = listCustomers;

  const [showImportModal, setShowImportModal] = useState(false);
  const handleImportCustomers = async (newClients) => {
    const now = Date.now();
    // Ids are assigned by the server (audit 2026-11-03): now + i ran ahead of
    // the clock and could collide with a card created in the UI meanwhile.
    const rows = newClients.map(c => ({ ...c, createdAt: now }));
    // Send in chunks of 500 to avoid timeout on large imports
    const CHUNK = 500;
    const total = { imported: 0, skippedDuplicates: 0, sqlErrors: 0 };
    try {
      for (let i = 0; i < rows.length; i += CHUNK) {
        const r = await apiFetch('/api/import-customers', { method: 'POST', body: { customers: rows.slice(i, i + CHUNK) } });
        total.imported += r?.imported || 0;
        total.skippedDuplicates += r?.skippedDuplicates || 0;
        total.sqlErrors += r?.sqlErrors?.length || 0;
      }
    } finally {
      // Earlier chunks are in even if a later one failed — show them.
      bumpListReload();
    }
    if (total.sqlErrors) {
      toast.error(t('clients.importSqlErrors', 'Частину карток не вдалося записати повністю: {n}. Повідомте адміністратора.').replace('{n}', String(total.sqlErrors)));
    }
    return total;
  };

  const handleExportClients = async () => {
    // Export honors current search/category but pulls the ENTIRE filtered
    // set from the server, not just what's currently paginated into the
    // list view. limit=MAX returns up to 200 rows per call — page through
    // until hasMore is false so exporting a 5K-customer tenant still
    // works. (Could bump server MAX_LIMIT later if needed.)
    const allRows = [];
    let page = 0;
    while (true) {
      const params = new URLSearchParams({ page: String(page), limit: '200' });
      if (search) params.set('q', search);
      if (categoryFilter) params.set('category', categoryFilter);
      if (originFilter) params.set('origin', originFilter);
      // eslint-disable-next-line no-await-in-loop
      const res = await apiFetch(`/api/analytics/customers-index?${params}`).catch(() => null);
      if (!res || !res.customers) break;
      allRows.push(...res.customers);
      if (!res.hasMore) break;
      page += 1;
      if (page > 100) break; // safety cap (20k customers)
    }
    const rows = allRows.map(c => {
      const visits = (appointments || []).filter(a => a.status === 'confirmed' && a.clientPhone === c.phone);
      const spent = (transactions || []).filter(t => t.customerPhone === c.phone).reduce((s, t) => s + (t.amount || 0), 0);
      const bday = c.birthDay && c.birthMonth
        ? `${c.birthDay}.${c.birthMonth}${c.birthYear ? `.${c.birthYear}` : ''}`
        : '';
      const genderLabel = c.gender === 'female' ? t('custModal.genderFemale') : c.gender === 'male' ? t('custModal.genderMale') : c.gender === 'other' ? t('custModal.genderOther') : '';
      return {
        name: c.name,
        phone: c.phone,
        additionalPhone: c.additionalPhone || '',
        email: c.email || '',
        gender: genderLabel,
        birthday: bday,
        category: c.category || '',
        visits: visits.length,
        spent,
        cardNumber: c.cardNumber || '',
        note: c.note || '',
      };
    });
    exportToExcel(rows, [
      { key: 'name', label: "Ім'я", width: 25 },
      { key: 'phone', label: 'Телефон', width: 15 },
      { key: 'additionalPhone', label: 'Дод. телефон', width: 15 },
      { key: 'email', label: 'Email', width: 22 },
      { key: 'gender', label: t('custModal.gender'), width: 10 },
      { key: 'birthday', label: 'День народження', width: 14 },
      { key: 'category', label: 'Категорія', width: 12 },
      { key: 'visits', label: 'Візитів', width: 10 },
      { key: 'spent', label: 'Витратив, ₴', width: 12 },
      { key: 'cardNumber', label: '№ картки', width: 14 },
      { key: 'note', label: 'Примітка', width: 25 },
    ], 'Клієнти');
  };

  /* Public pages — no auth required */
  if (currentView === 'terms') {
    return <Suspense fallback={<PageLoader />}><TermsPage /></Suspense>;
  }
  if (currentView === 'privacy') {
    return <Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>;
  }

  if (authLoading || loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0a1929', color:'white', fontSize:'1.2rem', gap:'0.75rem' }}>
        <div style={{ width:32, height:32, border:'3px solid rgba(255,255,255,0.2)', borderTopColor:'#5F9598', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
        {t('common.loading')}
      </div>
    );
  }

  if (!currentUser) {
    return <LoginPage />;
  }

  // Post-registration checkout screen (full-page, no sidebar)
  if (currentView === 'checkout') {
    return (
      <Suspense fallback={<PageLoader />}>
        <CheckoutPage onSkip={() => handleNavigate('dashboard')} />
      </Suspense>
    );
  }

  if (!businessType && !isSuperAdmin && !businessTypeLoaded && !tenantBlocked) {
    // Still loading business type — show spinner (but not if tenant is blocked)
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0a1929', color:'white', fontSize:'1.2rem', gap:'0.75rem' }}>
        <div style={{ width:32, height:32, border:'3px solid rgba(255,255,255,0.2)', borderTopColor:'#5F9598', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar currentView={currentView} onNavigate={handleNavigate} selectedDate={selectedDate} onDateChange={setSelectedDate} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} hasPermission={hasPermission} unreadMsgCount={unreadMsgCount} locations={locations} activeLocationId={activeLocationId} onLocationChange={setActiveLocationId} onSearchOpen={() => setShowGlobalSearch(true)} />

      {tenantBlocked && (
        <div className="tenant-blocked-overlay">
          <div className="tenant-blocked-card">
            <span style={{ fontSize: 48 }}>{tenantBlocked === 'deleting' ? '🗑️' : '🔒'}</span>
            <h2>
              {tenantBlocked === 'deleting' ? t('blocked.deleting', 'Акаунт заплановано до видалення')
                : tenantBlocked === 'deleted' ? t('blocked.deleted', 'Заклад видалено')
                : tenantBlocked === 'trial_expired' ? t('blocked.trialExpired', 'Пробний період закінчився')
                : tenantBlocked === 'payment_overdue' ? t('blocked.paymentOverdue', 'Підписка прострочена')
                : t('blocked.title', 'Акаунт заблоковано')}
            </h2>
            <p>
              {tenantBlocked === 'deleting' ? (
                deletionDate
                  ? t('blocked.deletingDesc', `Усі дані буде остаточно видалено ${deletionDate}. До цього моменту їх ще можна повернути — натисніть «Скасувати видалення».`)
                  : t('blocked.deletingDescNoDate', 'Видалення акаунта заплановано. Зверніться до адміністратора, якщо це помилка.')
              )
                : tenantBlocked === 'deleted' ? t('blocked.deletedDesc', 'Цей заклад і його дані видалено. Якщо це помилка, напишіть у підтримку.')
                : tenantBlocked === 'trial_expired' ? t('blocked.trialExpiredDesc', 'Оберіть тарифний план для продовження роботи')
                : tenantBlocked === 'payment_overdue' ? t('blocked.paymentOverdueDesc', 'Ваша підписка закінчилась. Будь ласка, продовжіть її для продовження роботи.')
                : tenantBlocked === 'blocked' ? t('blocked.subscriptionExpiredDesc', 'Ваша підписка закінчилась. Будь ласка, пролонгуйте її для продовження роботи.')
                : t('blocked.desc', 'Зверніться до адміністратора для відновлення доступу')}
            </p>
            {tenantBlocked === 'deleting' ? (
              deletionInfo?.scheduled ? (
                <button
                  className="btn btn-primary"
                  onClick={handleCancelDeletion}
                  disabled={cancellingDeletion}
                >
                  {cancellingDeletion
                    ? t('common.saving', 'Зачекайте…')
                    : t('blocked.cancelDeletion', 'Скасувати видалення')}
                </button>
              ) : (
                <a href="mailto:support@systemic-crm.com" className="btn btn-primary">{t('blocked.contact', 'Зв\'язатись з підтримкою')}</a>
              )
            ) : (tenantBlocked === 'trial_expired' || tenantBlocked === 'payment_overdue' || tenantBlocked === 'blocked') ? (
              <button className="btn btn-primary" onClick={() => { setTenantBlocked(null); handleNavigate('my_plan'); }}>{t('myPlan.upgrade', 'Обрати план')}</button>
            ) : (
              <a href="mailto:support@systemic-crm.com" className="btn btn-primary">{t('blocked.contact', 'Зв\'язатись з підтримкою')}</a>
            )}
            <button className="login-forgot-link" onClick={() => { auth.signOut(); setTenantBlocked(null); }}>{t('blocked.logout', 'Вийти')}</button>
          </div>
        </div>
      )}

      {showGlobalSearch && (
        <GlobalSearchModal
          appointments={allAppointments}
          staff={allStaff}
          onOpenCustomer={(c) => { setShowGlobalSearch(false); if (c) { handleView(c); } else { handleCreate(); } handleNavigate('clients'); }}
          onNavigate={(view) => { setShowGlobalSearch(false); handleNavigate(view); }}
          onOpenCalendar={(date) => { const d = new Date(date); d.setHours(0,0,0,0); setSelectedDate(d); }}
          onClose={() => setShowGlobalSearch(false)}
        />
      )}

      <div className="app-content">
        <div className="mobile-topbar">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <span className="hamburger-line" /><span className="hamburger-line" /><span className="hamburger-line" />
          </button>
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" className="mobile-topbar-logo" onClick={() => setCurrentView('dashboard')} style={{ cursor: 'pointer' }} />
          <span className="mobile-topbar-title" onClick={() => setCurrentView('dashboard')} style={{ cursor: 'pointer' }}>Systemic</span>
        </div>

        {isSuperAdmin && !impersonatedTenantId ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', color:'rgba(255,255,255,0.5)', fontSize:'1.1rem', textAlign:'center', padding:'2rem' }}>
            Оберіть тенант для перегляду даних
          </div>
        ) : <>

        {isSuperAdmin && impersonatedTenantId && readOnly && (
          <div style={{ background:'#f59e0b', color:'#000', textAlign:'center', padding:'6px 12px', fontSize:'0.85rem', fontWeight:600 }}>
            Режим перегляду — ви не є користувачем цього тенанта
          </div>
        )}

        {!pageReady && requiredGroups.length > 0 ? (
          <div className="page-skeleton"><div className="skeleton-bar" /><div className="skeleton-bar short" /><div className="skeleton-table"><div className="skeleton-row" /><div className="skeleton-row" /><div className="skeleton-row" /><div className="skeleton-row" /><div className="skeleton-row" /></div></div>
        ) : null}

        <Suspense fallback={<PageLoader />}>
        {currentView === 'dashboard' && (
          <DashboardPage
            staff={staff}
            services={services}
            products={products}
            stock={stock}
            unreadMsgCount={unreadMsgCount}
            onNavigate={handleNavigate}
            onOpenAppointment={(apt) => {
              setSelectedDate(new Date(apt.date + 'T00:00:00'));
              setCurrentView('calendar');
            }}
          />
        )}

        {currentView === 'clients' && (
          <>
            <header className="app-header">
              <div className="header-content">
                <div className="header-brand">
                  <div>
                    <h1>{t('clients.title')}</h1>
                    <p>{t('clients.subtitle')}</p>
                  </div>
                </div>
                <div className="header-stats">
                  <div className="stat-chip">
                    <span className="stat-num">{listTotal}</span>
                    <span className="stat-label">{t('clients.clientsCount')}</span>
                  </div>
                  <div className="stat-chip vip">
                    <span className="stat-num">{listVipCount}</span>
                    <span className="stat-label">VIP</span>
                  </div>
                </div>
                {hasPermission('clients_edit') && (
                  <button className="btn btn-add" onClick={handleCreate}>
                    {t('clients.addClient')}
                  </button>
                )}
              </div>
            </header>

            <main className="app-main">
              <div className="toolbar">
                <div className="search-wrapper">
                  <span className="search-icon">🔍</span>
                  <input type="text" className="search-input" inputMode="search"
                    placeholder={t('clients.searchPlaceholder')}
                    value={search} onChange={e => setSearch(e.target.value)} />
                  {search && (
                    <button className="search-clear" onClick={() => setSearch('')}>✕</button>
                  )}
                </div>
                <select className="filter-select" value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}>
                  <option value="">{t('clients.allCategories')}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                {originOptions.length > 0 && (
                  <select className="filter-select" value={originFilter}
                    onChange={e => setOriginFilter(e.target.value)} aria-label={t('clients.origin', 'Джерело')}>
                    <option value="">{t('clients.allOrigins', 'Усі джерела')}</option>
                    {originOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
                <div className="results-info">
                  {t('common.shown')}: <strong>{filtered.length}</strong> {t('common.of')} {listTotal}
                </div>
                {filtered.length > 0 && (
                  <button className="btn btn-outline btn-sm" onClick={handleExportClients} title="Експорт в Excel">
                    📥 Excel
                  </button>
                )}
                <button className="btn btn-outline btn-sm" onClick={() => setShowImportModal(true)}>
                  📤 {t('clients.import')}
                </button>
                <div className="view-toggle">
                  <button
                    className={`view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
                    onClick={() => { setViewMode('grid'); localStorage.setItem('clients_view', 'grid'); }}
                    title={t('clients.grid')}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    className={`view-toggle-btn${viewMode === 'list' ? ' active' : ''}`}
                    onClick={() => { setViewMode('list'); localStorage.setItem('clients_view', 'list'); }}
                    title={t('clients.list')}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>

              <CustomerGrid
                customers={filtered}
                categories={categories}
                importanceLevels={importanceLevels}
                onView={handleView}
                onEdit={hasPermission('clients_edit') ? handleEdit : undefined}
                onDelete={hasPermission('clients_delete') ? handleDeleteRequest : undefined}
                deletingId={deleteModal.checking ? deleteModal.id : null}
                onBook={handleBook}
                onOrder={hasPermission('orders_create') ? handleOrder : undefined}
                onSubscription={(loyaltyPrograms || []).find(p => p.id === 'default')?.subscriptions?.enabled ? handleSubscription : undefined}
                viewMode={viewMode}
                hasMore={listHasMore}
                loading={listLoading}
                onLoadMore={loadMoreClients}
              />
            </main>
          </>
        )}

        {currentView === 'categories' && hasPermission('clients_categories') && (
          <CategoriesPage
            categories={categories}
            onCategoriesChange={setCategories}
            importanceLevels={importanceLevels}
            onImportanceChange={setImportanceLevels}
          />
        )}

        {currentView === 'staff' && hasPermission('staff_view') && (
          <StaffPage
            staff={staff}
            onStaffChange={setStaff}
            services={services}
            serviceGroups={serviceGroups}
            appointments={appointments}
            salarySchemes={salarySchemes}
            locations={locations}
            activeLocationId={activeLocationId}
          />
        )}

        {currentView === 'schedule' && hasPermission('schedule_view') && (
          <SchedulePage
            staff={staff}
            onStaffChange={setStaff}
            appointments={appointments}
            scheduleTemplates={scheduleTemplates}
            onScheduleTemplatesChange={setScheduleTemplates}
            activeLocationId={activeLocationId}
            locations={locations}
          />
        )}

        {currentView === 'schedule_templates' && hasPermission('schedule_edit') && (
          <ScheduleTemplatesPage
            scheduleTemplates={scheduleTemplates}
            onScheduleTemplatesChange={setScheduleTemplates}
          />
        )}

        {currentView === 'services' && hasPermission('services_view') && (
          <ServicesPage
            serviceGroups={serviceGroups}
            onServiceGroupsChange={setServiceGroups}
            services={services}
            onServicesChange={setServices}
            appointments={appointments}
            products={products}
          />
        )}

        {currentView === 'calendar' && hasPermission('calendar_view') && (
          <CalendarPage
            staff={staff}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            appointments={appointments}
            onAppointmentsChange={setAppointmentsStamped}
            services={services}
            transactions={transactions}
            onTransactionsChange={setTransactionsStamped}
            products={products}
            warehouses={warehouses}
            stock={stock}
            onStockChange={setStock}
            tariffPlans={tariffPlans}
            bookingCustomer={bookingCustomer}
            onBookingCustomerClear={() => setBookingCustomer(null)}
            onOpenCustomerByPhone={handleOpenCustomerByPhone}
            onOpenCustomer={handleView}
            activeLocationId={activeLocationId}
            locations={locations}
            focusAppointmentId={focusAppointmentId}
            onFocusAppointmentClear={() => setFocusAppointmentId(null)}
            loyaltyPrograms={loyaltyPrograms}
            loyaltyBalances={loyaltyBalances}
            onLoyaltyBalancesChange={setLoyaltyBalances}
            subscriptionPlans={subscriptionPlans}
            coupons={coupons}
          />
        )}

        {currentView === 'tariff_plans' && hasPermission('settings_tariffs') && (
          <TariffPlansPage
            tariffPlans={tariffPlans}
            onTariffPlansChange={setTariffPlans}
            services={services}
            serviceGroups={serviceGroups}
            products={products}
            productCategories={productCategories}
            appointments={appointments}
            orders={orders}
          />
        )}

        {currentView === 'roles' && hasPermission('manage_roles') && (
          <RolesPage />
        )}

        {currentView === 'users' && hasPermission('manage_users') && (
          <UsersPage staff={staff} />
        )}

        {currentView === 'help' && (
          <HelpPage />
        )}

        {currentView === 'checkbox_settings' && hasPermission('settings_integrations') && (
          <CheckboxSettingsPage activeLocationId={activeLocationId} locations={locations} />
        )}

        {currentView === 'telegram_settings' && hasPermission('settings_integrations') && (
          <TelegramSettingsPage />
        )}

        {currentView === 'notifications_auto' && hasPermission('comm_settings_edit') && (
          <NotificationsAutoPage />
        )}

        {currentView === 'sms_settings' && hasPermission('settings_integrations') && (
          <SmsSettingsPage />
        )}

        {currentView === 'email_settings' && hasPermission('settings_integrations') && (
          <EmailSettingsPage />
        )}

        {currentView === 'nova_poshta_settings' && hasPermission('settings_integrations') && (
          <NovaPoshtaSettingsPage />
        )}

        {currentView === 'meest_express_settings' && hasPermission('settings_integrations') && (
          <MeestExpressSettingsPage />
        )}

        {currentView === 'rozetka_settings' && hasPermission('settings_integrations') && (
          <RozetkaSettingsPage />
        )}

        {currentView === 'prom_settings' && hasPermission('settings_integrations') && (
          <PromSettingsPage />
        )}

        {currentView === 'shopify_settings' && hasPermission('settings_integrations') && (
          <ShopifySettingsPage />
        )}

        {currentView === 'olx_settings' && hasPermission('settings_integrations') && (
          <OlxSettingsPage />
        )}

        {currentView === 'instagram_settings' && hasPermission('settings_integrations') && (
          <InstagramSettingsPage />
        )}

        {currentView === 'messenger_settings' && hasPermission('settings_integrations') && (
          <MessengerSettingsPage />
        )}

        {currentView === 'locations_settings' && hasPermission('settings_locations') && (
          <LocationsSettingsPage
            locations={locations}
            onLocationsChange={setLocations}
            staff={allStaff}
            warehouses={allWarehouses}
            orders={allOrders}
          />
        )}

        {currentView === 'online_booking_settings' && hasPermission('settings_booking') && (
          <OnlineBookingSettingsPage services={services} staff={allStaff} products={products} productCategories={productCategories} serviceGroups={serviceGroups} />
        )}

        {/* Заявки з сайту не потребують жодної групи даних із DataContext —
            сторінка читає лише свої налаштування. */}
        {currentView === 'leads_settings' && hasPermission('settings_integrations') && (
          <LeadsSettingsPage />
        )}

        {currentView === 'api_keys' && hasPermission('settings_integrations') && (
          <IntegrationKeysPage />
        )}

        {currentView === 'telephony_settings' && hasPermission('settings_integrations') && (
          <TelephonySettingsPage />
        )}

        {currentView === 'document_templates' && hasPermission('documents_templates') && (
          <DocumentTemplatesPage />
        )}

        {currentView === 'calls' && hasPermission('clients_view') && (
          <CallsPage onOpenByPhone={handleOpenCustomerByPhone} onCreateWithPhone={handleCreateWithPhone} />
        )}

        {currentView === 'audit_log' && hasPermission('audit_log_view') && (
          <AuditLogPage />
        )}

        {/* Задачі не потребують жодної групи даних із DataContext — сторінка
            тягне свій зріз сама, тож PAGE_GROUPS її не згадує і відкриття
            нічого не довантажує. */}
        {currentView === 'tasks' && hasPermission('tasks_view') && (
          <TasksPage activeLocationId={activeLocationId} />
        )}
        {/* Проєкти — дошки над тими самими задачами; дані тягне сама. */}
        {currentView === 'projects' && hasPermission('tasks_view') && (
          <ProjectsPage activeLocationId={activeLocationId} />
        )}

        {currentView === 'loyalty' && (
          <LoyaltyPage
            loyaltyPrograms={loyaltyPrograms}
            onLoyaltyProgramsChange={setLoyaltyPrograms}
            loyaltyBalances={loyaltyBalances}
            transactions={transactions}
            onTransactionsChange={setTransactionsStamped}
            coupons={coupons}
            onCouponsChange={setCoupons}
            subscriptionPlans={subscriptionPlans}
            onSubscriptionPlansChange={setSubscriptionPlans}
            services={services}
            appointments={appointments}
            staff={staff}
          />
        )}

        {currentView === 'communication' && (
          <CommunicationPage onOpenCustomer={handleView} onCreateCustomerFromThread={handleCreateFromThread} onUnreadCountChange={setUnreadMsgCount} />
        )}

        {currentView === 'finances' && hasPermission('finances_view') && (
          <FinancesPage
            staff={staff}
            onOpenCustomerByPhone={handleOpenCustomerByPhone}
            onNavigateToTransaction={handleNavigateToTransaction}
          />
        )}

        {currentView === 'cash_register' && hasPermission('cash_register') && (
          <CashRegisterPage
            transactions={transactions}
            activeLocationId={activeLocationId}
          />
        )}

        {currentView === 'inventory_products' && hasPermission('inventory_view') && (
          <InventoryProductsPage
            initialTab="products"
            products={products} onProductsChange={setProducts}
            productCategories={productCategories} onProductCategoriesChange={setProductCategories}
            warehouses={warehouses} onWarehousesChange={setWarehousesStamped}
            stock={stock}
            orders={orders}
            activeLocationId={activeLocationId}
          />
        )}

        {currentView === 'inventory_stock' && hasPermission('inventory_view') && (
          <InventoryStockPage
            products={products} productCategories={productCategories}
            warehouses={warehouses} allWarehouses={allWarehouses}
            stock={stock} onStockChange={setStock}
            activeLocationId={activeLocationId}
          />
        )}

        {currentView === 'inventory_purchases' && hasPermission('inventory_purchases') && (
          <InventoryPurchasesPage
            products={products}
            warehouses={warehouses}
            stock={stock} onStockChange={setStock}
            activeLocationId={activeLocationId}
            suppliers={suppliers} onSuppliersChange={setSuppliers}
          />
        )}

        {currentView === 'inventory_movements' && hasPermission('inventory_movements') && (
          <InventoryMovementsPage
            products={products} productCategories={productCategories}
            warehouses={warehouses}
            stock={stock} onStockChange={setStock}
            activeLocationId={activeLocationId}
          />
        )}

        {currentView === 'production' && hasPermission('production_view') && (
          <ProductionPage
            techCards={techCards} onTechCardsChange={setTechCards}
            products={products} productCategories={productCategories}
            warehouses={warehouses}
            stock={stock} onStockChange={setStock}
            activeLocationId={activeLocationId}
            hasPermission={hasPermission}
          />
        )}

        {currentView === 'pos' && hasPermission('pos_sell') && (
          <QuickSalePage
            products={products} productCategories={productCategories}
            warehouses={warehouses}
            stock={stock} onStockChange={setStock}
            transactions={transactions} onTransactionsChange={setTransactionsStamped}
            loyaltyPrograms={loyaltyPrograms}
            loyaltyBalances={loyaltyBalances} onLoyaltyBalancesChange={setLoyaltyBalances}
            activeLocationId={activeLocationId}
            locations={locations}
            hasPermission={hasPermission}
            tariffPlans={(tariffPlans || []).filter(tp => tp.isActive !== false)}
            viewScope={userProfile?.viewScope} currentUid={currentUser?.uid}
            coupons={coupons}
          />
        )}

        {currentView === 'orders' && hasPermission('orders_view') && (
          <OrdersPage
            orders={orders} onOrdersChange={setOrdersStamped}
            products={products} productCategories={productCategories}
            warehouses={warehouses}
            stock={stock} onStockChange={setStock}
            transactions={transactions} onTransactionsChange={setTransactionsStamped}
            staff={staff}
            hasPermission={hasPermission}
            tariffPlans={(tariffPlans || []).filter(tp => tp.isActive !== false)}
            orderingCustomer={orderingCustomer}
            onOrderingCustomerClear={() => setOrderingCustomer(null)}
            locations={locations}
            activeLocationId={activeLocationId}
            focusOrderId={focusOrderId}
            onFocusOrderClear={() => setFocusOrderId(null)}
            loyaltyPrograms={loyaltyPrograms}
            loyaltyBalances={loyaltyBalances}
            onLoyaltyBalancesChange={setLoyaltyBalances}
            coupons={coupons}
            viewScope={userProfile?.viewScope} currentUid={currentUser?.uid}
            onOpenCustomerByPhone={handleOpenCustomerByPhone}
          />
        )}

        {currentView === 'reports' && hasPermission('reports_view') && (
          <ReportsPage
            transactions={allTransactions}
            appointments={allAppointments}
            staff={allStaff}
            services={services}
            serviceGroups={serviceGroups}
            products={products}
            productCategories={productCategories}
            warehouses={allWarehouses}
            orders={allOrders}
            onOpenCustomerByPhone={handleOpenCustomerByPhone}
            activeLocationId={activeLocationId}
            locations={locations}
            suppliers={suppliers}
            subscriptionPlans={subscriptionPlans}
          />
        )}

        {currentView === 'salary_schemes' && hasPermission('salary_edit') && (
          <SalarySchemesPage
            salarySchemes={salarySchemes}
            onSalarySchemesChange={setSalarySchemes}
            staff={staff}
            services={services}
          />
        )}

        {currentView === 'salary_calc' && hasPermission('salary_view') && (
          <SalaryCalcPage
            staff={allStaff}
            salarySchemes={salarySchemes}
            appointments={allAppointments}
            transactions={allTransactions}
            activeLocationId={activeLocationId}
            locations={locations}
            services={services}
          />
        )}

        {currentView === 'financial_result' && hasPermission('financial_result') && (
          <FinancialResultPage
            appointments={allAppointments}
            transactions={allTransactions}
            fixedExpenses={fixedExpenses}
            onFixedExpensesChange={setFixedExpenses}
            activeLocationId={activeLocationId}
            locations={locations}
            staff={staff}
            products={products}
            orders={allOrders}
            onNavigateToSource={handleNavigateToTransaction}
          />
        )}

        {currentView === 'business_monitor' && hasPermission('business_monitor') && (
          <BusinessMonitorPage
            appointments={allAppointments}
            transactions={allTransactions}
            staff={staff}
            services={services}
            fixedExpenses={fixedExpenses}
            products={products}
            stock={stock}
            orders={allOrders}
            activeLocationId={activeLocationId}
            locations={locations}
          />
        )}

        {currentView === 'my_plan' && (
          <MyPlanPage />
        )}

        {currentView === 'checkout' && (
          <CheckoutPage onSkip={() => handleNavigate('dashboard')} />
        )}

        {!KNOWN_VIEWS.has(currentView) && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'60vh', color:'rgba(255,255,255,0.4)', gap:'0.75rem' }}>
            <div style={{ fontSize:'3rem', opacity:0.4 }}>404</div>
            <div style={{ fontSize:'1rem' }}>Сторінку не знайдено</div>
          </div>
        )}
        </Suspense>
        </>}
      </div>

      {modalOpen && (
        <CustomerModal
          customer={editingCustomer}
          prefill={editingCustomer ? null : modalPrefill}
          categories={categories}
          importanceLevels={importanceLevels}
          appointments={appointments}
          transactions={transactions}
          staff={staff}
          services={services}
          orders={orders}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          onOpenAppointment={(apt) => {
            setModalOpen(false);
            setSelectedDate(new Date(apt.date + 'T00:00:00'));
            handleNavigate('calendar');
          }}
          initialTab={modalInitialTab}
          initialMode={modalInitialMode}
          loyaltyPrograms={loyaltyPrograms}
          loyaltyBalances={loyaltyBalances}
          onLoyaltyBalancesChange={setLoyaltyBalances}
          subscriptionPlans={subscriptionPlans}
          onTransactionsChange={setTransactionsStamped}
          products={products}
        />
      )}
      {/* Вікно «дзвонить» — саме вирішує, чи підключено телефонію. */}
      <Suspense fallback={null}>
        <CallPopup onOpenByPhone={handleOpenCustomerByPhone} onCreateWithPhone={handleCreateWithPhone} />
      </Suspense>
      {showImportModal && (
        <ImportCustomersModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImportCustomers}
        />
      )}
      {deleteModal.open && !deleteModal.checking && (
        deleteModal.hasRecords ? (
          <DeleteModal
            title={t('clients.cannotDeleteTitle')}
            message={t('clients.cannotDeleteMessage')}
            itemName={deleteModal.name}
            onConfirm={() => setDeleteModal({ open: false, id: null })}
            onClose={() => setDeleteModal({ open: false, id: null })}
            confirmLabel={t('clients.understood')}
            hideCancel
          />
        ) : (
          <DeleteModal
            title={t('clients.deleteTitle')}
            message={t('clients.deleteMessage')}
            itemName={deleteModal.name}
            onConfirm={handleDeleteConfirm}
            onClose={() => setDeleteModal({ open: false, id: null })}
            warning={t('clients.deleteWarning')}
          />
        )
      )}
    </div>
  );
}

function App() {
  const { impersonatedTenantId, isSuperAdmin, tenantId } = useAuth();

  // Skip data loading when there's no tenant context to load against:
  //   • Superadmin without an impersonated tenant
  //   • Regular user whose token has no tenantId yet (new signup before
  //     tenant provisioning completes, suspended/deleted tenant, edge
  //     cases on mobile after auth)
  // Without this second branch every tenant-scoped endpoint returned 403
  // and cascaded into a 30+ request error storm at first paint.
  const skipLoad = (isSuperAdmin && !impersonatedTenantId) || !tenantId;
  // Superadmin is a member of this tenant → full access; otherwise read-only
  const isOwnTenant = impersonatedTenantId && tenantId === impersonatedTenantId;
  const readOnly = impersonatedTenantId ? !isOwnTenant : false;

  return (
    <I18nProvider>
      <ToastProvider>
        <DataProvider key={impersonatedTenantId || tenantId || 'default'} readOnly={readOnly} skipLoad={skipLoad}>
          <AppContent />
        </DataProvider>
      </ToastProvider>
    </I18nProvider>
  );
}

export default App;
